import VideoCoverDialogHost from 'components/Composer/Content/Media/VideoCoverDialogHost'
import ComposerPrompt from 'components/Composer/Prompt'
import { ComposerContentDialog } from 'components/ComposerContentDialog'
import ConnectedSnackbar from 'components/ConnectedSnackbar'
import ContentPopup from 'components/ContentPopup'
import ContentPreviewWindow from 'components/ContentPreviewWindow'
import ConnectedFileDialog from 'components/FileDialog'
import FileUploadManager from 'components/MultiUploadManager'
import PostProgressOverlay from 'components/PostProgress'
import ProfitwellScript from 'components/ProfitwellScript'
import SaveFeedDialog from 'components/SaveFeedDialog'
import TokensChecker from 'components/TokensChecker'
import { EndTrialPopup, TrialExpiredPopup } from 'components/Trial'
import UpgradePopup from 'components/UpgradePopup'
import { LEGACY_URL } from 'config'
import { Fragment, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigationType } from 'react-router'
import { updateIntercom } from 'services/tracking/login'
import { closeComposerContentDialog } from 'services/ui'
import { composerContentDialogSelector } from 'services/ui/selectors'
import { getUserSubscription } from 'services/users/actions'
import { currentUserSelector } from 'services/users/selectors'
import { User } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { isMobileScreen } from 'utils/browser'
import { AppLayout } from '../AppLayout'
import ComposerContentDeepLink from './ComposerContentDeepLink'
import HotjarTracker from '../HotjarTracker'
import MobileWarningDialog from '../MobileWarningDialog'
import { Rewardful } from '../Rewardful'

export interface AuthenticatedAppProps {
  user?: User
}

export function AuthenticatedApp() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const user = useSelector(currentUserSelector)
  const composerContentDialog = useSelector(composerContentDialogSelector)
  const location = useLocation()
  const navigationType = useNavigationType()

  const prevLocationRef = useRef(location)
  const [isMobile, setIsMobile] = useState(isMobileScreen())

  useEffect(() => {
    const onResize = () => {
      setIsMobile(isMobileScreen())
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  useEffect(() => {
    if (navigationType !== 'POP' && (!location.state?.modal)) {
      prevLocationRef.current = location
    }
  }, [navigationType, location])

  useEffect(() => {
    const update = () => {
      navigator.sendBeacon(`${LEGACY_URL}/v3/intercom`)
    }
    window.addEventListener('unload', update, false)
    return () => {
      window.removeEventListener('unload', update)
    }
  }, [])

  useEffect(() => {
    dispatch(updateIntercom())
    dispatch(getUserSubscription())
  }, [dispatch])

  const modalActive = Boolean(location.state?.modal && prevLocationRef.current !== location)
  const hideSidebar = location.pathname.startsWith('/onboarding')
  const trialExpired = user?.account.trial === 'expired' && !user?.account.hasPaymentInfo
  const isNoBlurPage = location.pathname.startsWith('/settings/subscription') || location.pathname.startsWith('/onboarding')

  return (
    <Fragment>
      <AppLayout
        hidden={modalActive}
        blurred={trialExpired && !isNoBlurPage}
        hideSidebar={hideSidebar}
      />
      <ConnectedSnackbar />
      <UpgradePopup />
      <SaveFeedDialog />
      <ContentPreviewWindow />
      <TokensChecker />
      <ContentPopup />
      <ConnectedFileDialog />
      <FileUploadManager />
      <PostProgressOverlay />
      <ComposerPrompt />
      <ComposerContentDialog
        open={composerContentDialog.open}
        initialTab={composerContentDialog.tab}
        imageUrl={composerContentDialog.imageUrl}
        conversation={composerContentDialog.conversation}
        prompt={composerContentDialog.prompt}
        kind={composerContentDialog.kind}
        searchMode={composerContentDialog.searchMode}
        onClose={() => dispatch(closeComposerContentDialog())}
      />
      <VideoCoverDialogHost />
      <ComposerContentDeepLink />
      <TrialExpiredPopup />
      <EndTrialPopup />
      <Rewardful />
      {isMobile && (
        <MobileWarningDialog />
      )}
      {__PROD__ && (
        <Fragment>
          <ProfitwellScript />
          <HotjarTracker />
        </Fragment>
      )}
    </Fragment>
  )
}

export default AuthenticatedApp
